import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Split Excel Sheets V4
 *
 * Same semantics as `split_excel_sheets` but uses ExcelJS streaming under the
 * hood so the full worksheet XML is never materialised as a single JS string —
 * safe for workbooks whose uncompressed sheet data exceeds V8's ~512 MB
 * string-length limit (e.g. AS400 exports).
 *
 * Single-sheet passthrough: when exactly one worksheet survives the filter,
 * the original File is returned unchanged — no re-upload, no format
 * conversion.
 *
 * Multi-sheet: one child File is uploaded per matching sheet, linked to the
 * parent via `parentFileId`.
 *
 * Every output item also gets a Document wrapping its File 1:1 (created
 * lazily, reusing an existing Document if one already wraps that File) —
 * downstream AI steps such as `csv_transform_v4`, `extract_document_content_v4`,
 * and `classify_document_v2` take a Document input, not a raw File.
 */
export const splitExcelSheetsV4Step = defineStepConfig({
  type: "split_excel_sheets_v4",
  name: "Split Excel Sheets V4",
  description:
    "Split an Excel file into one file per worksheet (ExcelJS streaming, safe for large files)",
  category: "Transformation",

  inputSchema: z.object({
    file: entityOfType(SystemEntity.CORE_FILE).describe(
      "The Excel file to split into sheets.",
    ),
  }),

  outputSchema: z.object({
    file: z
      .array(entityOfType(SystemEntity.CORE_FILE))
      .describe("Child file per sheet (or the original when single sheet)"),
    document: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe(
        "Document wrapping the same File as `file` (index-aligned) — wire this into Document-typed downstream steps",
      ),
    sheetName: z.array(z.string()).describe("Worksheet name per item"),
    sheetIndex: z
      .array(z.number())
      .describe("0-based worksheet index per item"),
  }),

  configSchema: z.object({
    sheetFilter: z
      .string()
      .optional()
      .describe(
        "Optional regex pattern to filter sheet names (e.g. '^Data' to only include sheets starting with 'Data')",
      ),
    skipEmptySheets: z
      .boolean()
      .optional()
      .default(true)
      .describe("Skip worksheets that have no data rows"),
    outputFormat: z
      .enum(["csv", "xlsx"])
      .optional()
      .default("csv")
      .describe(
        "Format for child files when splitting multiple sheets: CSV or XLSX. Ignored for single-sheet passthrough.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
